import React, { useEffect, useRef } from 'react'
import { Input, Popconfirm } from 'antd'
import type {
  PublicProfileProduct,
  PublicVideoRequestDescriptionSuggestion,
  RequestOccasion,
} from 'apollo-gql'
import { useTranslation } from 'lib/i18n'
import { Body } from 'stories/UI/Typography/Body.stories'
import { BodySmall } from 'stories/UI/Typography/BodySmall.stories'
import { H6 } from 'stories/UI/Typography/H6.stories'

import { Button } from 'components/UI/Button/Button'
import useRequestInstruction from 'hooks/useRequestInstruction'
import useRequestOccasions from 'hooks/useRequestOccasions'
import useVideoRequestDescriptionSuggestions from 'hooks/useVideoRequestDescriptionSuggestions'

import styles from './RequestInstructions.module.css'

const { TextArea } = Input

interface SuggestionProps {
  videoRequestDescriptionSuggestionId: string
  instructions: string
}

type RequestInstructionsProps = {
  profileName: string
  profileId: string
  onChange: () => void
  value: string
  textareaStyle?: React.CSSProperties
  id: string
  onChangeSuggestion: (suggestion: SuggestionProps) => void
  onClearInstructions: () => void
  requestOccasionId?: string
  profileProduct: PublicProfileProduct
  charCount: string
  showSavedIndicator?: boolean
}

const focusEndOfText = (e: React.FocusEvent<HTMLTextAreaElement>) =>
  e.currentTarget.setSelectionRange(
    e.currentTarget.value.length,
    e.currentTarget.value.length,
  )

const SavedIndicator = () => {
  const { t } = useTranslation()

  return (
    <div data-cy="instructionsSavedText" className={styles.savedIndicator}>
      <BodySmall className={styles.charCount}>
        {t('RequestInstructions.instructionsSaved', 'Saved!')}
      </BodySmall>
    </div>
  )
}

const RequestInstructions: React.FC<RequestInstructionsProps> = ({
  profileName,
  profileId,
  onChange,
  value,
  textareaStyle = {},
  id,
  onChangeSuggestion,
  onClearInstructions,
  requestOccasionId = '',
  profileProduct,
  charCount,
  showSavedIndicator,
}) => {
  const productId = profileProduct?.product?.id
  const { t } = useTranslation()
  const textAreaRef = useRef(null)

  const { requestOccasions = [] } = useRequestOccasions({
    productType: profileProduct?.product?.productType,
    first: 1000,
  })
  const occasionsIds = requestOccasions.map((oc: RequestOccasion) => oc.id)

  const { videoRequestDescriptionSuggestions = [], loading } =
    useVideoRequestDescriptionSuggestions({ profileId, productId })

  const suggestions = videoRequestDescriptionSuggestions.filter(
    (s: PublicVideoRequestDescriptionSuggestion) => {
      if (!s.requestOccasionId && occasionsIds.includes(s.requestOccasionId))
        return true
      return s.requestOccasionId === requestOccasionId
    },
  )

  const { hasWrittenInstruction, setWrittenInstruction } =
    useRequestInstruction()

  const description = suggestions[0]?.description
  const suggestionId = suggestions[0]?.id

  useEffect(() => {
    // Select the first suggestion after they are loaded from api, unless a instruction is already present

    if (!value) {
      if (!loading) {
        textAreaRef.current.state.value = description
        onChangeSuggestion({
          videoRequestDescriptionSuggestionId: suggestionId,
          instructions: description,
        })
      }
      if (
        Array.isArray(suggestions) &&
        suggestions.length > 0 &&
        requestOccasionId
      ) {
        onChangeSuggestion({
          videoRequestDescriptionSuggestionId: suggestionId,
          instructions: description,
        })
      }
      setWrittenInstruction(false)
    }
    if (value && !loading) {
      if (!hasWrittenInstruction || value.trim().length === 0) {
        onChangeSuggestion({
          videoRequestDescriptionSuggestionId: suggestionId,
          instructions: description,
        })
        setWrittenInstruction(false)
        return
      }

      onChangeSuggestion({
        videoRequestDescriptionSuggestionId: suggestionId,
        instructions: value,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestionId])

  return (
    <>
      <H6 className={styles.textTitle}>
        {t(
          'RequestInstructions.instructionTitle1',
          'What do you want {{profileName}} to do and/or say in the video?',
          { profileName },
        )}
      </H6>
      <Body className={styles.textBody}>
        {t(
          'RequestInstructions.instructionDescription1',
          'Provide any specific words or wishes that will help make the video extra special.',
        )}
      </Body>

      <div
        className={styles.textSuggestion}
        role="button"
        onClick={() => {
          onChangeSuggestion({
            videoRequestDescriptionSuggestionId: '',
            instructions: value,
          })
        }}
        tabIndex={0}
      >
        <TextArea
          id={id}
          onChange={onChange}
          value={value}
          style={textareaStyle}
          ref={textAreaRef}
          onFocus={() => {
            return focusEndOfText
          }}
        />
      </div>

      <div className={styles.countAndClear}>
        <BodySmall className={styles.charCount}>{charCount} </BodySmall>

        {showSavedIndicator && <SavedIndicator />}

        {value && value.trim().length > 0 && (
          <Popconfirm
            title={t(
              'RequestInstructions.clearConfirm',
              'Are you sure you want to clear the instructions?',
            )}
            onConfirm={onClearInstructions}
            placement="topRight"
            okButtonProps={{ className: styles.confirmButtons }}
            cancelButtonProps={{ className: styles.confirmButtons }}
          >
            <Button className={styles.clearButton} type="black" size="tiny">
              {t('RequestInstructions.clear', 'Clear')}
            </Button>
          </Popconfirm>
        )}
      </div>
    </>
  )
}

export default RequestInstructions
